import {
  Content,
  ContentHeader,
  Header,
  InfoCard,
  Link,
  Page,
  Progress,
} from '@backstage/core-components';
import type { Entity } from '@backstage/catalog-model';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import {
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import AssessmentIcon from '@material-ui/icons/Assessment';
import DescriptionIcon from '@material-ui/icons/Description';
import ExtensionIcon from '@material-ui/icons/Extension';
import ReportProblemIcon from '@material-ui/icons/ReportProblem';
import TimelineIcon from '@material-ui/icons/Timeline';
import { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

type ProductSummary = {
  title: string;
  entityName: string;
  description: string;
  owner: string;
  status: string;
  stack: string;
  deliveryMaturity: string;
  maxFeatureClass: string;
  blueprintStatus: string;
  firstVerticalSlice: string;
  proofLevel: string;
  initiatingSurface: string;
  readbackSurface: string;
  namespace: string;
  workflow: string;
  releaseMode: string;
  gaps: string[];
};

function readString(value: unknown, emptyValue: string): string {
  return typeof value === 'string' && value.length > 0 ? value : emptyValue;
}

function readAnnotation(
  annotations: Record<string, string> | undefined,
  key: string,
  emptyValue = 'not declared',
): string {
  return readString(annotations?.[key], emptyValue);
}

function splitAnnotationList(value: string): string[] {
  if (!value || value === 'not declared') {
    return [];
  }

  return value
    .split('|')
    .map(item => item.trim())
    .filter(Boolean);
}

function productFromCatalogEntity(entity: Entity): ProductSummary | undefined {
  const annotations = entity.metadata.annotations ?? {};
  const entityName = annotations['c4ci.io/registry-repo-id'];
  if (!entityName) {
    return undefined;
  }

  return {
    title: readString(entity.metadata.title, entityName),
    entityName,
    description: readString(
      entity.metadata.description,
      'No product description declared in catalog.',
    ),
    owner: readString(entity.spec?.owner, 'unassigned'),
    status: readString(entity.spec?.lifecycle, 'unknown'),
    stack: readAnnotation(annotations, 'c4ci.io/product-stack'),
    deliveryMaturity: readAnnotation(annotations, 'c4ci.io/delivery-maturity'),
    maxFeatureClass: readAnnotation(annotations, 'c4ci.io/max-feature-class'),
    blueprintStatus: readAnnotation(annotations, 'c4ci.io/blueprint-status'),
    firstVerticalSlice: readAnnotation(
      annotations,
      'c4ci.io/first-vertical-slice-status',
    ),
    proofLevel: readAnnotation(annotations, 'c4ci.io/proof-level'),
    initiatingSurface: readAnnotation(
      annotations,
      'c4ci.io/initiating-surface',
    ),
    readbackSurface: readAnnotation(annotations, 'c4ci.io/readback-surface'),
    namespace: readAnnotation(annotations, 'c4ci.io/release-namespace'),
    workflow: readAnnotation(annotations, 'c4ci.io/deploy-workflow'),
    releaseMode: readAnnotation(annotations, 'c4ci.io/release-mode'),
    gaps: splitAnnotationList(
      readAnnotation(annotations, 'c4ci.io/blocking-gaps', ''),
    ),
  };
}

function featureClassRank(featureClass: string): number {
  const match = /^F([0-9]+)$/i.exec(featureClass);
  return match ? Number(match[1]) : -1;
}

function highestFeatureClass(products: ProductSummary[]): string {
  return products.reduce((highest, product) => {
    if (featureClassRank(product.maxFeatureClass) > featureClassRank(highest)) {
      return product.maxFeatureClass;
    }
    return highest;
  }, 'unknown');
}

const useStyles = makeStyles(theme => ({
  hero: {
    padding: theme.spacing(4),
    borderRadius: theme.shape.borderRadius * 3,
    background:
      'linear-gradient(135deg, rgba(251,140,0,0.16), rgba(0,121,107,0.14))',
    border: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(4),
  },
  chipRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  statCard: {
    height: '100%',
  },
  productCard: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  metaGrid: {
    display: 'grid',
    gap: theme.spacing(1),
    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
    marginTop: theme.spacing(2),
  },
  metaItem: {
    padding: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.action.hover,
  },
  linkRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(3),
  },
  footer: {
    marginTop: 'auto',
  },
  gapList: {
    display: 'grid',
    gap: theme.spacing(1.5),
  },
  gapItem: {
    padding: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
  },
}));

const ProductMetric = ({
  label,
  value,
}: {
  label: string;
  value: string;
}) => {
  const classes = useStyles();

  return (
    <Box className={classes.metaItem}>
      <Typography variant="caption" color="textSecondary">
        {label}
      </Typography>
      <Typography variant="body2">{value}</Typography>
    </Box>
  );
};

export const FactoryProductsPage = () => {
  const classes = useStyles();
  const catalogApi = useApi(catalogApiRef);
  const [products, setProducts] = useState<ProductSummary[]>([]);
  const [catalogState, setCatalogState] = useState<'loading' | 'loaded' | 'error'>(
    'loading',
  );
  const [catalogError, setCatalogError] = useState<string | undefined>();
  const allGaps = products.flatMap(product =>
    product.gaps.map(gap => ({ gap, product: product.entityName })),
  );
  const maxFeatureClass = highestFeatureClass(products);

  useEffect(() => {
    let isActive = true;

    catalogApi
      .getEntities({
        filter: { kind: 'Component' },
        order: { field: 'metadata.name', order: 'asc' },
      })
      .then(response => {
        if (!isActive) {
          return;
        }

        const catalogProducts = response.items
          .map(productFromCatalogEntity)
          .filter((product): product is ProductSummary => Boolean(product));
        if (catalogProducts.length === 0) {
          setProducts([]);
          setCatalogState('error');
          setCatalogError(
            'No C4CI product entities with project metadata annotations were returned by catalog.',
          );
          return;
        }

        setProducts(catalogProducts);
        setCatalogState('loaded');
        setCatalogError(undefined);
      })
      .catch(error => {
        if (!isActive) {
          return;
        }

        setProducts([]);
        setCatalogState('error');
        setCatalogError(error instanceof Error ? error.message : String(error));
      });

    return () => {
      isActive = false;
    };
  }, [catalogApi]);

  return (
    <Page themeId="home">
      <Header
        title="Factory Products"
        subtitle="Product posture, deployment shape, and gap radar from live Backstage catalog metadata"
      />
      <Content>
        <Box className={classes.hero}>
          <div className={classes.chipRow}>
            <Chip color="primary" label="Registry-backed" />
            <Chip
              label={
                catalogState === 'loaded'
                  ? 'Live catalog data'
                  : 'Catalog data required'
              }
              variant="outlined"
            />
            <Chip label="No synthetic product cards" variant="outlined" />
          </div>
          <Typography variant="h4" component="h2" gutterBottom>
            A focused operating map for what we have, what works now, and what
            needs proof next.
          </Typography>
          <Typography variant="body1" color="textSecondary">
            The portal mirrors registered product repos into catalog and reads
            posture fields from their generated project metadata annotations.
            If the catalog cannot provide those fields, this page shows the
            missing data instead of inventing replacement cards.
          </Typography>
        </Box>

        {catalogState === 'loading' && <Progress />}
        {catalogError && (
          <Box mb={3}>
            <InfoCard title="Live Catalog Data Required">
              <Typography variant="body2" color="textSecondary">
                Factory Products did not render product cards because live
                catalog metadata was unavailable: {catalogError}
              </Typography>
            </InfoCard>
          </Box>
        )}

        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <InfoCard className={classes.statCard} title="Registered Products">
              <Box display="flex" alignItems="center" mb={2}>
                <ExtensionIcon color="primary" />
              </Box>
              <Typography variant="h3">{products.length}</Typography>
              <Typography variant="body2" color="textSecondary">
                Product repos represented by live catalog entities.
              </Typography>
            </InfoCard>
          </Grid>
          <Grid item xs={12} md={4}>
            <InfoCard className={classes.statCard} title="Highest Feature Class">
              <Box display="flex" alignItems="center" mb={2}>
                <AssessmentIcon color="primary" />
              </Box>
              <Typography variant="h3">{maxFeatureClass}</Typography>
              <Typography variant="body2" color="textSecondary">
                Highest feature class declared in product project metadata.
              </Typography>
            </InfoCard>
          </Grid>
          <Grid item xs={12} md={4}>
            <InfoCard className={classes.statCard} title="Known Blocking Gaps">
              <Box display="flex" alignItems="center" mb={2}>
                <ReportProblemIcon color="primary" />
              </Box>
              <Typography variant="h3">{allGaps.length}</Typography>
              <Typography variant="body2" color="textSecondary">
                Blocking gaps declared by product project metadata.
              </Typography>
            </InfoCard>
          </Grid>
        </Grid>

        <Box mt={4}>
          <ContentHeader title="Product Operating Map" />
          <Grid container spacing={3}>
            {products.map(product => (
              <Grid item xs={12} lg={6} key={product.entityName}>
                <InfoCard className={classes.productCard} title={product.title}>
                  <Typography variant="body2" color="textSecondary" paragraph>
                    {product.description}
                  </Typography>
                  <div className={classes.chipRow}>
                    <Chip label={product.entityName} size="small" />
                    <Chip label={product.deliveryMaturity} size="small" />
                    <Chip
                      color="primary"
                      label={product.maxFeatureClass}
                      size="small"
                    />
                  </div>
                  <div className={classes.metaGrid}>
                    <ProductMetric label="Owner" value={product.owner} />
                    <ProductMetric label="Status" value={product.status} />
                    <ProductMetric label="Blueprint" value={product.blueprintStatus} />
                    <ProductMetric
                      label="Vertical Slice"
                      value={product.firstVerticalSlice}
                    />
                    <ProductMetric label="Proof" value={product.proofLevel} />
                    <ProductMetric label="Namespace" value={product.namespace} />
                    <ProductMetric label="Workflow" value={product.workflow} />
                    <ProductMetric label="Release Mode" value={product.releaseMode} />
                  </div>
                  <Box mt={3}>
                    <Typography variant="subtitle2">Stack</Typography>
                    <Typography variant="body2" color="textSecondary">
                      {product.stack}
                    </Typography>
                  </Box>
                  <Box mt={3}>
                    <Typography variant="subtitle2">Runtime surfaces</Typography>
                    <Typography variant="body2" color="textSecondary">
                      Initiates at <code>{product.initiatingSurface}</code> and
                      reads back at <code>{product.readbackSurface}</code>.
                    </Typography>
                  </Box>
                  <Box mt={3}>
                    <Typography variant="subtitle2">
                      Blocking gaps ({product.gaps.length})
                    </Typography>
                    {product.gaps.length > 0 ? (
                      product.gaps.map(gap => (
                        <Typography
                          color="textSecondary"
                          key={gap}
                          variant="body2"
                        >
                          {gap}
                        </Typography>
                      ))
                    ) : (
                      <Typography color="textSecondary" variant="body2">
                        No blocking gaps declared in product metadata.
                      </Typography>
                    )}
                  </Box>
                  <div className={`${classes.linkRow} ${classes.footer}`}>
                    <Button
                      color="primary"
                      component={RouterLink}
                      startIcon={<AssessmentIcon />}
                      to={`/catalog/default/component/${product.entityName}`}
                      variant="outlined"
                    >
                      Catalog
                    </Button>
                    <Button
                      color="primary"
                      component={RouterLink}
                      startIcon={<DescriptionIcon />}
                      to={`/docs/default/component/${product.entityName}`}
                      variant="outlined"
                    >
                      TechDocs
                    </Button>
                    <Button
                      color="primary"
                      component={RouterLink}
                      startIcon={<TimelineIcon />}
                      to="/factory-workflows"
                      variant="contained"
                    >
                      Workflows
                    </Button>
                  </div>
                </InfoCard>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Box mt={4}>
          <ContentHeader title="Gap Radar" />
          <InfoCard>
            <div className={classes.gapList}>
              {allGaps.length > 0 ? (
                allGaps.map(({ gap, product }, index) => (
                  <Box className={classes.gapItem} key={`${product}-${gap}`}>
                    <Typography variant="subtitle2">
                      {index + 1}. {gap}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Owned by{' '}
                      <Link
                        component={RouterLink}
                        to={`/catalog/default/component/${product}`}
                      >
                        {product}
                      </Link>
                    </Typography>
                  </Box>
                ))
              ) : (
                <Typography variant="body2" color="textSecondary">
                  No blocking gaps are declared in the live product metadata.
                </Typography>
              )}
            </div>
            <Box mt={3}>
              <Divider />
            </Box>
            <Box mt={3}>
              <Typography variant="body2" color="textSecondary">
                Gap closure should happen in the product repos. Factory Portal
                should surface, launch, and audit the work without becoming a
                second source of truth.
              </Typography>
            </Box>
          </InfoCard>
        </Box>
      </Content>
    </Page>
  );
};
